import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ICellRendererParams,
  IServerSideGetRowsRequest
} from 'ag-grid-enterprise';
import { from } from 'rxjs';
import { VendorInviteForm } from 'src/app/classes/form-groups/vendor-invite-form';
import {
  Company,
  CompanyComplianceType,
  CompanyOperationState,
  CompanyVendor,
  Vendor
} from 'src/app/classes/models';
import { InvitationContact } from 'src/app/classes/models/invitation-contact';
import { VendorInvite } from 'src/app/classes/models/vendor-invite';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { SelectComplianceProgramModalComponent } from 'src/app/modals/select-compliance-program-modal/select-compliance-program-modal.component';
import { VendorInviteModalComponent } from 'src/app/modals/vendor-invite-modal/vendor-invite-modal.component';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorInviteService } from 'src/app/services/vendor-invite.service';

@Component({
  selector: 'app-client-vive-vendors',
  templateUrl: './client-vive-vendors.component.html',
  styleUrls: ['./client-vive-vendors.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientViveVendorsComponent implements OnInit, OnChanges {
  constructor(
    private Router: Router,
    private ToastService: ToastService,
    private VendorInviteService: VendorInviteService,
    private ModalService: NgbModal,
    private CompanyService: CompanyService,
    private UtilityService: UtilityService
  ) {}

  @Input() CompanyOperationStates?: CompanyOperationState[];
  @Input() CompanyId!: number;
  @Input() AdditionalQueryData?: GenericObject;
  @Input() OriginalQueryData?: GenericObject;
  @Input() country?: 'US' | 'CA';

  @Output() dataGridQueryChanged = new EventEmitter<{
    GridQuery: GenericObject;
    GridRequest: IServerSideGetRowsRequest;
  }>();

  CompanyVendors!: CompanyVendor[];
  Company!: Company;

  suppliedGridQueryData?: GenericObject;

  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['view', 'plane']
  };

  TooltipMap: {
    [key in Extract<GridActionColumnActions, 'view' | 'plane'>]: string;
  } = {
    view: 'View Vendor',
    plane: 'Invite Vendor'
  };

  inviteVendor() {
    const { componentInstance, result } = this.ModalService.open(
      SelectComplianceProgramModalComponent,
      { backdrop: 'static', size: 'md' }
    );
    Object.assign(componentInstance, { CompanyId: this.CompanyId });

    from(result).subscribe({
      next: (complianceTypeId: CompanyComplianceType) => {
        if (complianceTypeId) {
          this.sendInvite(complianceTypeId);
        }
      }
    });
  }

  sendInvite(CompanyComplianceTypeId: CompanyComplianceType | null) {
    const { componentInstance, result } = this.ModalService.open(
      VendorInviteModalComponent,
      { backdrop: 'static', size: 'md' }
    );

    componentInstance.CompanyId = this.CompanyId;

    from(result).subscribe({
      next: (VendorInviteFormGroup: VendorInviteForm) => {
        const { value: VendorInvite } = VendorInviteFormGroup;

        Object.assign(VendorInvite, {
          CompanyComplianceTypeId: CompanyComplianceTypeId
        });

        this.VendorInviteService.saveVendorInvite(VendorInvite).subscribe({
          next: () => {
            this.ToastService.addSuccessToast(
              'Success',
              'Vendor Invite sent successfully!'
            );
          }
        });
      }
    });
  }

  viewVendor(rowData: any) {
    if (!rowData) {
      throw this.ToastService.addErrorToast(
        'Error',
        'No CompanyVendor data found in row',
        { returnError: true }
      );
    }

    const { id } = rowData;

    if (!id) {
      throw this.ToastService.addErrorToast('Error', 'No Vendor ID Found', {
        returnError: true
      });
    }

    this.Router.navigate([
      'client',
      this.CompanyId,
      'vendors',
      'vive-vendor',
      id
    ]);
  }

  getCalculatedColumnActions: ({
    rowData
  }: {
    rowData: Vendor;
  }) => GridActionColumnActions[] = ({ rowData }: { rowData: Vendor }) => {
    const { InvitationContacts } = rowData;

    if (InvitationContacts && InvitationContacts.length > 0) {
      return ['view'];
    }

    return ['view', 'plane'];
  };

  action({
    action,
    rowData,
    cellRendererParams
  }: {
    action: GridActionColumnActions;
    rowData: Vendor;
    cellRendererParams: ICellRendererParams;
  }) {
    if (action === 'view') {
      this.viewVendor(rowData as CompanyVendor);
    }
    if (action === 'plane') {
      const { id: VendorId, legalName, dba, ComplianceContact } = rowData;
      if (!ComplianceContact) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Compliance Contact for invite.',
          { returnError: true }
        );
      }
      const { email } = ComplianceContact;

      const { componentInstance, result } = this.ModalService.open(
        SelectComplianceProgramModalComponent,
        { backdrop: 'static', size: 'md' }
      );

      Object.assign(componentInstance, { CompanyId: this.CompanyId });

      from(result).subscribe({
        next: (complianceTypeId: CompanyComplianceType) => {
          if (!complianceTypeId) {
            return;
          }

          const InvitationContactRecord = new InvitationContact({
            CompanyId: this.CompanyId,
            VendorId,
            legalName,
            dba,
            email
          });

          const VendorInviteRecord = new VendorInvite({
            CompanyId: this.CompanyId,
            inviteEmail: email,
            InvitationContact: InvitationContactRecord,
            CompanyComplianceTypeId: complianceTypeId as any
          });

          this.VendorInviteService.saveVendorInvite(
            VendorInviteRecord
          ).subscribe({
            next: () => {
              if (!rowData.InvitationContacts) {
                Object.assign(rowData, { InvitationContacts: [] });
              }

              const { InvitationContacts } = rowData;
              InvitationContacts!.push(InvitationContactRecord);

              if (cellRendererParams.refreshCell) {
                cellRendererParams.refreshCell();
              }

              this.ToastService.addSuccessToast(
                'Success',
                'Invitation successfully sent!'
              );
            }
          });
        }
      });
    }
  }

  onResetFilters(event: boolean) {
    if (event) {
      if (this.OriginalQueryData) {
        this.suppliedGridQueryData = this.OriginalQueryData;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { AdditionalQueryData } = changes;

    if (AdditionalQueryData) {
      const { currentValue } = AdditionalQueryData;
      this.suppliedGridQueryData = currentValue;
    }
  }

  ngOnInit(): void {
    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Client Id.', {
        returnError: true
      });
    }

    if (!this.country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing country information.',
        { returnError: true }
      );
    }

    this.CompanyService.getCompany(
      this.CompanyId,
      this.UtilityService.stringifyObjectsInQueryObject({
        excludedAttributes: ['logo']
      })
    ).subscribe({
      next: (Company) => {
        if (!Company) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client information.',
            { returnError: true }
          );
        }

        this.Company = Company;
      }
    });
  }
}
